/**
 * Onboarding state management using localStorage
 */

export interface OnboardingState {
  // Current progress
  currentStep: 1 | 2 | 3 | 4 | 'completed';

  // Which steps have been completed
  completedSteps: number[];

  // When user skipped onboarding (ISO timestamp)
  skippedAt: string | null;

  // Form created during onboarding (used for subsequent steps)
  formId: string | null;
  formName: string | null;

  // Telegram connection status for the wizard form
  telegramConnected: boolean;

  // Test submission sent
  testSubmitted: boolean;

  // Email configured (optional step)
  emailConfigured: boolean;

  // Version for future migrations
  version: number;
}

export const ONBOARDING_STORAGE_KEY = 'formto_onboarding';
export const ONBOARDING_VERSION = 1;

export const DEFAULT_ONBOARDING_STATE: OnboardingState = {
  currentStep: 1,
  completedSteps: [],
  skippedAt: null,
  formId: null,
  formName: null,
  telegramConnected: false,
  testSubmitted: false,
  emailConfigured: false,
  version: ONBOARDING_VERSION,
};

/**
 * Get the current onboarding state from localStorage
 */
export function getOnboardingState(): OnboardingState | null {
  try {
    const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

/**
 * Save onboarding state to localStorage
 */
export function setOnboardingState(state: OnboardingState): void {
  localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state));
}

/**
 * Clear onboarding state from localStorage
 */
export function clearOnboardingState(): void {
  localStorage.removeItem(ONBOARDING_STORAGE_KEY);
}

/**
 * Check if onboarding wizard should be shown
 * Returns true for new users or users with incomplete onboarding
 */
export function shouldShowOnboarding(): boolean {
  const state = getOnboardingState();

  // Never shown onboarding -> show it
  if (!state) return true;

  // Already completed -> don't show
  if (state.currentStep === 'completed') return false;

  // Skipped recently (within 24 hours) -> don't redirect automatically
  if (state.skippedAt) {
    const skippedTime = new Date(state.skippedAt).getTime();
    const now = Date.now();
    const hoursSinceSkip = (now - skippedTime) / (1000 * 60 * 60);
    if (hoursSinceSkip < 24) return false;
  }

  return true;
}

/**
 * Mark onboarding as skipped (user clicked "Skip for now")
 */
export function markOnboardingSkipped(): void {
  const current = getOnboardingState() || DEFAULT_ONBOARDING_STATE;
  setOnboardingState({
    ...current,
    skippedAt: new Date().toISOString(),
  });
}

/**
 * Mark onboarding as fully completed
 */
export function markOnboardingCompleted(): void {
  const current = getOnboardingState() || DEFAULT_ONBOARDING_STATE;
  setOnboardingState({
    ...current,
    currentStep: 'completed',
    completedSteps: [1, 2, 3, 4],
  });
}

/**
 * Check if onboarding has been completed
 */
export function isOnboardingCompleted(): boolean {
  const state = getOnboardingState();
  return state?.currentStep === 'completed';
}

/**
 * Get the number of completed steps
 */
export function getCompletedStepsCount(): number {
  const state = getOnboardingState();
  return state?.completedSteps.length || 0;
}
